/**
 * `/session/:sessionId` (#440, #362; UX §4.7, §4.7b): one exam-prep problem at a time, then the end.
 *
 * What the learner demonstrated is decided by the server (the kernel grades; `deriveIndependence` decides independence).
 * This screen only shows the item as the server returns it and sends what the learner does. Three rules it holds:
 *
 * - An answer DaVinci cannot read is a re-prompt, never "wrong"; the input is kept.
 * - Help comes only after an attempt, or after "I don't know yet" (FRONTEND_DESIGN §2.1); each shows its cost before it is
 *   used. The server enforces this too; the screen simply does not offer a hint before an incorrect attempt.
 * - The end is a list in words: no count, score, bar or praise.
 *
 * Maths is rendered from the server's TeX (`statementTex`) or from `previewTexOfAnswer`, always left to right.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { ExamPrepItem, ExamPrepSessionResponse } from '@davinci/contracts';
import { previewTexOfAnswer } from '@davinci/domain';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { UntrustedText } from '../homework/untrusted-text.js';
import { ReasonLine } from './prep-page.js';
import { usePrep, type IncorrectReason, type UnparseableReason } from './prep-labels.js';
import { Tex } from './tex.js';

type Session = NonNullable<ExamPrepSessionResponse['session']>;

/** A fresh idempotency key per intended action: kept across a retry of the same action, renewed once it succeeds. */
function useActionKey(): { get: () => string; renew: () => void } {
  const key = useRef<string | undefined>(undefined);
  return {
    get: () => (key.current ??= crypto.randomUUID()),
    renew: () => {
      key.current = undefined;
    },
  };
}

function Steps({ items, cursor }: { readonly items: readonly ExamPrepItem[]; readonly cursor: number }): React.JSX.Element {
  const t = usePrep();
  return (
    <ol className="prep-steps" aria-label={t.steps}>
      {items.map((item, index) => {
        const state = index < cursor ? 'done' : index === cursor ? 'current' : 'next';
        return (
          <li
            key={item.itemId}
            className="prep-step"
            data-state={state}
            {...(state === 'current' ? { 'aria-current': 'step' as const } : {})}
          >
            <span className="visually-hidden">
              {state === 'done' ? t.stepDone(index + 1) : state === 'current' ? t.stepNow(index + 1) : t.stepNext(index + 1)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function ItemView({
  item,
  onItem,
  onNext,
}: {
  readonly item: ExamPrepItem;
  readonly onItem: (item: ExamPrepItem) => void;
  readonly onNext: () => void;
}): React.JSX.Element {
  const t = usePrep();
  const api = useApi();
  const [answer, setAnswer] = useState(item.stem?.answerTemplate ?? '');
  const attemptKey = useActionKey();
  const hintKey = useActionKey();
  const unknownKey = useActionKey();

  const attempt = useMutation({
    mutationFn: () => api.submitExamPrepAttempt(item.itemId, attemptKey.get(), { answerText: answer }),
    onSuccess: (response) => {
      attemptKey.renew();
      onItem(response.item);
    },
  });
  const hint = useMutation({
    mutationFn: () => api.requestExamPrepHint(item.itemId, hintKey.get()),
    onSuccess: (updated) => {
      hintKey.renew();
      onItem(updated);
    },
  });
  const unknown = useMutation({
    mutationFn: () => api.declareExamPrepUnknown(item.itemId, unknownKey.get()),
    onSuccess: (updated) => {
      unknownKey.renew();
      onItem(updated);
    },
  });

  const last = item.attempts.at(-1);
  const open = item.state === 'open';
  const stem = item.stem;
  const tier = t.tier[item.reason.tier];
  const returning =
    item.reason.returning === 'retention_check' ? t.reasonBack : item.reason.returning === 'not_yet' ? t.reasonNotYet : null;
  const preview = previewTexOfAnswer(answer);
  const unparseableReason = last?.unparseableReason;
  const incorrectReason = last?.incorrectReason;

  return (
    <article className="prep-card" aria-labelledby={`item-${item.itemId}`}>
      <h2 id={`item-${item.itemId}`} className="visually-hidden">
        <UntrustedText text={item.topic.sectionName ?? t.topicFallback} />
      </h2>
      <p className="prep-muted">
        {item.reason.sheet === null && item.reason.question === null ? (
          t.fromSemester
        ) : (
          <>
            {t.from} <ReasonLine sheet={item.reason.sheet} question={item.reason.question} />
          </>
        )}
        {': '}
        {tier}
      </p>
      {returning !== null ? <p className="prep-muted">{returning}</p> : null}

      {item.kind === 'proof_pointer' && item.pointer !== null ? (
        <>
          <p>
            <strong>{t.pointerTitle}</strong>
          </p>
          <p>
            {t.pointerGoBefore}
            {/* Sheet title and question label are untrusted text (#440 boundary review): UntrustedText isolates them and marks bidi controls. */}
            {item.pointer.sheet === null && item.pointer.question === null ? (
              t.pointerSheet
            ) : (
              <UntrustedText text={[item.pointer.sheet, item.pointer.question].filter((x) => x !== null).join(', ')} />
            )}
            {t.pointerGoAfter}
          </p>
          <p className="prep-muted">
            <UntrustedText text={item.pointer.why} />
          </p>
        </>
      ) : null}

      {stem !== null ? (
        <>
          <p>{stem.taskKind === 'witness' ? t.witness(t.domain[stem.domain]) : t.counterexample(t.domain[stem.domain])}</p>
          <Tex tex={stem.statementTex} display />
        </>
      ) : null}

      {open && stem !== null ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (answer.trim() !== '') attempt.mutate();
          }}
        >
          <label htmlFor={`answer-${item.itemId}`}>{t.answerLabel}</label>
          <input
            id={`answer-${item.itemId}`}
            className="prep-answer"
            type="text"
            dir="ltr"
            lang="en"
            autoComplete="off"
            spellCheck={false}
            value={answer}
            placeholder={stem.answerTemplate}
            onChange={(event) => setAnswer(event.target.value)}
          />
          <div className="prep-preview" aria-live="polite" aria-label={t.previewLabel}>
            {preview !== null ? <Tex tex={preview} /> : <span className="prep-muted">{t.previewNone}</span>}
          </div>
          <button type="submit" className="prep-pill" disabled={attempt.isPending || answer.trim() === ''}>
            {t.submit}
          </button>
        </form>
      ) : null}

      {open && last?.result === 'unparseable' ? (
        <p role="status">
          {t.unparseableTitle} {unparseableReason !== null && unparseableReason !== undefined && unparseableReason !== 'null'
            ? t.unparseable[unparseableReason as UnparseableReason]
            : t.unparseableOther}
        </p>
      ) : null}
      {open && last?.result === 'incorrect' ? (
        <div role="status">
          <p>
            <strong>{t.incorrect}</strong>
          </p>
          {incorrectReason !== null && incorrectReason !== undefined && incorrectReason !== 'null' ? (
            <p>{t.incorrectWhy[incorrectReason as Exclude<IncorrectReason, 'null'>]}</p>
          ) : null}
        </div>
      ) : null}

      {open && item.help.hint !== null ? <p>{t.hint(item.help.hint.bound)}</p> : null}

      {open && stem !== null && (item.help.canDeclareUnknown || (item.help.canAskForHint && last?.result === 'incorrect')) ? (
        <div className="prep-help">
          {item.help.canAskForHint && last?.result === 'incorrect' && item.help.hint === null ? (
            <div className="prep-help-row">
              <button type="button" disabled={hint.isPending} aria-describedby={`hint-cost-${item.itemId}`} onClick={() => hint.mutate()}>
                {t.hintButton}
              </button>
              <span id={`hint-cost-${item.itemId}`} className="prep-muted">
                {t.hintCost}
              </span>
            </div>
          ) : null}
          {item.help.canDeclareUnknown ? (
            <div className="prep-help-row">
              <button type="button" disabled={unknown.isPending} aria-describedby={`unknown-cost-${item.itemId}`} onClick={() => unknown.mutate()}>
                {t.unknownButton}
              </button>
              <span id={`unknown-cost-${item.itemId}`} className="prep-muted">
                {t.unknownCost}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {item.state === 'solved' ? (
        <p role="status">
          <strong>{item.outcome === 'with_help' ? t.correctWithHelp : t.correct}</strong>
        </p>
      ) : null}
      {item.state === 'revealed' ? (
        <div role="status">
          <p>
            {t.revealed}
            {item.help.reveal !== null ? (
              <>
                : <code dir="ltr">{item.help.reveal.answerText}</code>
              </>
            ) : null}
          </p>
          <p className="prep-muted">{t.revealedAfter}</p>
        </div>
      ) : null}

      {attempt.isError ? <FailureNotice error={attempt.error} onRetry={() => attempt.mutate()} /> : null}
      {hint.isError ? <FailureNotice error={hint.error} onRetry={() => hint.mutate()} /> : null}
      {unknown.isError ? <FailureNotice error={unknown.error} onRetry={() => unknown.mutate()} /> : null}

      {!open ? (
        <button type="button" className="prep-pill" onClick={onNext}>
          {t.next}
        </button>
      ) : null}
    </article>
  );
}

function Summary({ session }: { readonly session: Session }): React.JSX.Element {
  const t = usePrep();
  const api = useApi();
  const key = useActionKey();
  const started = useRef(false);
  const finish = useMutation({ mutationFn: () => api.finishExamPrepSession(session.sessionId, key.get()) });
  const { mutate } = finish;
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    mutate();
  }, [mutate]);

  if (finish.isError) return <FailureNotice error={finish.error} onRetry={() => finish.mutate()} />;
  if (finish.data === undefined) return <p>{t.finishing}</p>;
  return (
    <section aria-labelledby="prep-done">
      <h2 id="prep-done">{t.done}</h2>
      <ul className="prep-outcomes">
        {finish.data.items.map((entry) => (
          <li key={entry.itemId}>
            <strong>{t.outcome[entry.outcome]}</strong>
            {' · '}
            {entry.topic.sectionName !== null ? <UntrustedText text={entry.topic.sectionName} /> : t.topicFallback}
            {entry.sheet !== null || entry.question !== null ? (
              <span className="prep-muted">
                {' · '}
                <ReasonLine sheet={entry.sheet} question={entry.question} />
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      <Link className="prep-pill" to="/exam-prep">
        {t.backToPlan}
      </Link>
    </section>
  );
}

function Runner({ session }: { readonly session: Session }): React.JSX.Element {
  const t = usePrep();
  const [items, setItems] = useState<readonly ExamPrepItem[]>(session.items);
  const [cursor, setCursor] = useState(session.finished ? session.items.length : Math.min(Math.max(session.index, 0), session.items.length));
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const item = items[cursor];

  return (
    <>
      {session.showDesirableDifficultyNotice && !noticeDismissed ? (
        <div className="prep-note" role="note">
          <p>{t.difficulty}</p>
          <button type="button" onClick={() => setNoticeDismissed(true)}>
            {t.dismiss}
          </button>
        </div>
      ) : null}
      <Steps items={items} cursor={cursor} />
      {item === undefined ? (
        <Summary session={session} />
      ) : (
        <ItemView
          key={item.itemId}
          item={item}
          onItem={(updated) => setItems((previous) => previous.map((existing) => (existing.itemId === updated.itemId ? updated : existing)))}
          onNext={() => setCursor((value) => value + 1)}
        />
      )}
    </>
  );
}

export function SessionPage(): React.JSX.Element {
  const t = usePrep();
  const api = useApi();
  const { sessionId = '' } = useParams();
  const query = useQuery({
    queryKey: queryKeys.examPrepSession(sessionId),
    queryFn: () => api.getExamPrepSession(sessionId),
    // The runner holds the live copy of the items; a refetch must not replace it under the learner's hands.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  return (
    <div className="page prep">
      <h1>{t.sessionHeading}</h1>
      {query.isError ? <FailureNotice error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.isPending ? <p>{t.loading}</p> : null}
      {query.data !== undefined && query.data.session === null ? <p>{t.noSession}</p> : null}
      {query.data?.session != null ? <Runner session={query.data.session} /> : null}
    </div>
  );
}
